import { useEffect, useMemo, useRef, useState } from "react";
import { HiChevronDown } from "react-icons/hi";
import {
  COUNTRY_PHONE_PREFIXES,
  getCountryByCode,
} from "../../../data/countryPhonePrefixes";
import "./PhoneCountrySelect.css";

const THEMES = {
  apply: {
    wrap: "phone-country-select phone-country-select--apply",
    wrapOpen: "phone-country-select--open",
    trigger: "phone-country-select__trigger phone-country-select__trigger--apply",
    value: "phone-country-select__value",
    chevron: "phone-country-select__chevron",
    menu: "phone-country-select__menu phone-country-select__menu--apply",
    option: "phone-country-select__option phone-country-select__option--apply",
    optionSelected: "phone-country-select__option--selected",
    optionDial: "phone-country-select__option-dial",
    optionName: "phone-country-select__option-name",
  },
  contact: {
    wrap: "phone-country-select phone-country-select--contact",
    wrapOpen: "phone-country-select--open",
    trigger: "phone-country-select__trigger phone-country-select__trigger--contact",
    value: "phone-country-select__value",
    chevron: "phone-country-select__chevron",
    menu: "phone-country-select__menu phone-country-select__menu--contact",
    option: "phone-country-select__option phone-country-select__option--contact",
    optionSelected: "phone-country-select__option--selected",
    optionDial: "phone-country-select__option-dial",
    optionName: "phone-country-select__option-name",
  },
  quote: {
    wrap: "phone-country-select phone-country-select--quote",
    wrapOpen: "phone-country-select--open",
    trigger: "phone-country-select__trigger phone-country-select__trigger--quote",
    value: "phone-country-select__value",
    chevron: "phone-country-select__chevron",
    menu: "phone-country-select__menu phone-country-select__menu--quote",
    option: "phone-country-select__option phone-country-select__option--quote",
    optionSelected: "phone-country-select__option--selected",
    optionDial: "phone-country-select__option-dial",
    optionName: "phone-country-select__option-name",
  },
};

function matchesCountrySearch(country, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return country.name.toLowerCase().includes(q);
}

export default function PhoneCountrySelect({
  value,
  onChange,
  disabled = false,
  theme = "apply",
  id = "phone-country-select",
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const searchInputRef = useRef(null);
  const selected = getCountryByCode(value);
  const classes = THEMES[theme] ?? THEMES.apply;

  const filteredCountries = useMemo(
    () =>
      COUNTRY_PHONE_PREFIXES.filter((country) =>
        matchesCountrySearch(country, search)
      ),
    [search]
  );

  useEffect(() => {
    if (!open) {
      setSearch("");
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    function handlePointerDown(event) {
      if (!wrapRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  function selectCountry(code) {
    onChange(code);
    setSearch("");
    setOpen(false);
  }

  function toggleOpen() {
    if (disabled) return;
    setOpen((prev) => !prev);
  }

  return (
    <div
      className={`${classes.wrap}${open ? ` ${classes.wrapOpen}` : ""}`}
      ref={wrapRef}
    >
      <button
        type="button"
        id={id}
        className={classes.trigger}
        onClick={toggleOpen}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Country calling code, ${selected?.dial ?? "+1"} selected`}
      >
        <span className={classes.value}>{selected?.dial ?? "+1"}</span>
        <HiChevronDown className={classes.chevron} aria-hidden="true" />
      </button>

      {open ? (
        <div className="phone-country-select__menu-panel">
          <input
            ref={searchInputRef}
            type="search"
            className="phone-country-select__search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onClick={(event) => event.stopPropagation()}
            placeholder="Search country…"
            aria-label="Search country by name"
            autoComplete="off"
          />
          <ul
            className={classes.menu}
            role="listbox"
            aria-label="Country calling codes"
          >
            {filteredCountries.length === 0 ? (
              <li className="phone-country-select__empty" role="presentation">
                No countries found
              </li>
            ) : (
              filteredCountries.map((country) => {
                const isSelected = country.code === value;

                return (
                  <li key={country.code} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`${classes.option}${
                        isSelected ? ` ${classes.optionSelected}` : ""
                      }`}
                      onClick={() => selectCountry(country.code)}
                    >
                      <span className={classes.optionDial}>{country.dial}</span>
                      <span className={classes.optionName}>{country.name}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
